import React, { Fragment, useState } from "react";
import styled from "styled-components";

import { groupAddressListByType } from "./components/AddressListItem";
import AggEtcIcon from "@src/assets/auctionDetail/aggEtcIcon.png";
import BuidIcon from "@src/assets/auctionDetail/buidIcon.png";
import EtcIcon from "@src/assets/auctionDetail/etcIcon.png";
import LandIcon from "@src/assets/auctionDetail/landIcon.png";

import {
    IAuctionSaleItemProps,
    IGroupedAddressList,
    ISaleItem,
    ISaleItemGroupProps,
    ISaleItemRowProps,
    ITotalPriceAreaProps,
} from "./lib/DataTable.type";
import { AnimatedWrapper } from "./lib/animationStyle";
import ConvertPyeongButton from "@src/components/button/ConvertPyeongButton";
import BuildingRegisterOpenButton from "./components/BuildingRegisterOpenButton";
import LandRegisterOpenButton from "./components/LandRegisterOpenButton";

const TotalPriceArea = ({ totalPyong, totalPrice }: ITotalPriceAreaProps) => {
    return (
        <tr className="tdPinkContainer">
            <td className="tdContainer">총</td>
            <td colSpan={2}>
                <div className="tdEstPositionContainer">
                    <div className="tdEstPosition area">
                        <p>면적</p>
                        <p>{totalPyong || "-"}</p>
                    </div>
                    <div className="tdEstPosition estPrice">
                        <p>감정가</p>
                        <p>{totalPrice || "-"}</p>
                    </div>
                </div>
            </td>
        </tr>
    );
};

const getSaleItemRowSpan = (item: ISaleItem) => {
    if (item?.land_right_info?.no) return 3;
    if (item.shr_sale_flag) return 4;
    if (item?.est_price) return 3;
    return 2;
};

const SaleItemRow = ({ item, convertType, jibun }: ISaleItemRowProps) => {
    const splitDetail = item?.detail_info?.split("/");
    const trimDetail = splitDetail?.map((ele) => ele.trim()).filter(Boolean);

    const showBuildingRegisterOpenButton = (type: string) => {
        return ["건물", "집합건물"].includes(type);
    };

    const showLandRegisterOpenButton = (type: string) => {
        return ["토지", "대지권", "대지지분"].includes(type);
    };

    return (
        <Fragment>
            <tr className="tdWrapper">
                <td rowSpan={getSaleItemRowSpan(item)} className="tdContainer">
                    {item?.no}
                </td>
                <TdContainerS $type={item.type}>
                    <span>{item?.type}</span>
                    <p className="greyText01">{item?.detail_type}</p>
                </TdContainerS>
                <td className="tdContainer">{item?.address}</td>
            </tr>
            {item.est_price && (
                <tr className="tdPinkContainer">
                    <td colSpan={2}>
                        <div className="tdEstPositionContainer">
                            <div className="tdEstPosition area">
                                <p>면적</p>
                                <p>{convertType === "meter" ? item?.area.square : item?.area.pyong}</p>
                            </div>
                            <div className="tdEstPosition estPrice">
                                <p>감정가</p>
                                <p>
                                    {item.est_price} <br />
                                    {item.price_land_per_pyong && <>(평당 {item.price_land_per_pyong})</>}
                                </p>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
            {item.shr_sale_flag && (
                <tr className="tdBlueContainer">
                    <td colSpan={2}>
                        <div>
                            <BlueText>해당 {item.type}(은)는 지분 매각입니다.</BlueText>
                            <p>{item.shr_sale_info}</p>
                        </div>
                    </td>
                </tr>
            )}
            <tr className="tdWrapper">
                <td colSpan={2} className="tdGreyContainer">
                    <p>{trimDetail?.join(" / ") ?? "-"}</p>
                    {showLandRegisterOpenButton(item?.type) && item?.pnu && <LandRegisterOpenButton />}
                    {showBuildingRegisterOpenButton(item?.type) && <BuildingRegisterOpenButton />}
                </td>
            </tr>
        </Fragment>
    );
};

const SaleItemGroup = ({
    items,
    convertType,
    jibun,
    totalPyong,
    totalPrice,
    isCollectiveBuilding = false,
    totalLandRightAreaPyong,
}: ISaleItemGroupProps) => {
    if (!items || items.length === 0) return null;

    return (
        <>
            {items.map((item) => (
                <SaleItemRow key={item.no} item={item} convertType={convertType} jibun={jibun} />
            ))}
            <TotalPriceArea totalPyong={totalPyong} totalPrice={totalPrice} />

            {isCollectiveBuilding &&
                items.map((item) => (
                    <Fragment key={`land-right-${item.no}`}>
                        {item.land_right_info_list?.map((v) => (
                            <Fragment key={v.no}>
                                <tr className="tdWrapper">
                                    <td className="tdContainer" rowSpan={2}>
                                        {v.no}
                                    </td>
                                    <TdContainerS $type={v?.type}>
                                        <span>{v?.type}</span>
                                        <p className="greyText01">{v?.detail_type}</p>
                                    </TdContainerS>
                                    <td className="tdContainer">{v?.address}</td>
                                </tr>
                                <tr className="tdPinkContainer">
                                    <td colSpan={2}>
                                        <div className="tdEstPositionContainer">
                                            <div className="tdEstPosition area">
                                                <p>면적</p>
                                                <p>{convertType === "meter" ? v?.area?.square : v?.area?.pyong}</p>
                                            </div>
                                            <div className="tdEstPosition estPrice">
                                                <p>감정가</p>
                                                <p>-</p>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </Fragment>
                        ))}
                    </Fragment>
                ))}
            {isCollectiveBuilding && <TotalPriceArea totalPyong={totalLandRightAreaPyong} />}
        </>
    );
};

const AuctionSaleItemPreview = ({
    auctionData,
    jibun,
}: {
    auctionData: IAuctionSaleItemProps["saleItem"];
    jibun: IAuctionSaleItemProps["jibun"];
}) => {
    const { land, buid, etc, agg_bd } = auctionData?.sale_items_cnt || {};
    const { sale_items_list, sale_items_total_info } = auctionData;

    const groupedAddressList: IGroupedAddressList = groupAddressListByType(sale_items_list);
    const [convertType, setConvertType] = useState<"pyeong" | "meter">("meter");

    const handleConvertType = (type: "pyeong" | "meter") => {
        setConvertType(type);
    };

    return (
        <AnimatedWrapper>
            <TitleWrapper>
                <h4>매각물건 현황</h4>
                <div className="cntContainer">
                    <>
                        {!land?.includes("00") && (
                            <div className="cntBox land">
                                <img src={LandIcon} alt="토지" width={28} height={28} />
                                <p className="cntText">{land}</p>
                            </div>
                        )}
                        {!buid?.includes("00") && (
                            <div className="cntBox build">
                                <img src={BuidIcon} alt="건물" width={28} height={28} />
                                <p className="cntText">{buid}</p>
                            </div>
                        )}
                        {!etc?.includes("00") && (
                            <div className="cntBox etc">
                                <img src={EtcIcon} alt="기타" width={28} height={28} />
                                <p className="cntText">{etc}</p>
                            </div>
                        )}
                        {!agg_bd?.includes("00") && (
                            <div className="cntBox colBuild">
                                <img src={AggEtcIcon} alt="집합건물" width={28} height={28} />
                                <p className="cntText">{agg_bd}</p>
                            </div>
                        )}
                    </>

                    <div className="convertButton">
                        <ConvertPyeongButton
                            handleConvert={() => handleConvertType(convertType === "meter" ? "pyeong" : "meter")}
                        />
                    </div>
                </div>
                <div className="chartPosition">
                    <table className="chartWrapper">
                        <colgroup>
                            <col width="34px" />
                            <col width="72px" />
                            <col width="auto" />
                        </colgroup>
                        <thead>
                            <tr className="thWrapper">
                                <th className="thContainer">번호</th>
                                <th className="thContainer">구분</th>
                                <th className="thContainer">소재지</th>
                            </tr>
                        </thead>
                        <tbody>
                            <SaleItemGroup
                                items={groupedAddressList.토지}
                                convertType={convertType}
                                totalPyong={sale_items_total_info?.total_land_area_pyong}
                                totalPrice={sale_items_total_info?.total_land_est_price}
                            />
                            <SaleItemGroup
                                items={groupedAddressList.집합건물}
                                convertType={convertType}
                                jibun={jibun}
                                totalPyong={sale_items_total_info?.total_agg_bd_area_pyong}
                                totalPrice={sale_items_total_info?.total_agg_bd_est_price}
                                isCollectiveBuilding={true}
                                totalLandRightAreaPyong={sale_items_total_info?.total_land_right_area_pyong}
                            />
                            <SaleItemGroup
                                items={groupedAddressList.건물}
                                convertType={convertType}
                                jibun={jibun}
                                totalPyong={sale_items_total_info?.total_build_area_pyong}
                                totalPrice={sale_items_total_info?.total_build_est_price}
                            />
                            <SaleItemGroup
                                items={groupedAddressList.기타}
                                convertType={convertType}
                                totalPyong={sale_items_total_info?.total_other_area_pyong}
                                totalPrice={sale_items_total_info?.total_other_est_price}
                            />
                        </tbody>
                    </table>
                </div>
            </TitleWrapper>
            <TotalWrapper>
                <div className="totalContainer">
                    <div className="totalBox">
                        <div className="descBox">
                            <span>총 감정가</span>
                            <b>{auctionData?.est_price}</b>
                        </div>
                        <div className="descBox">
                            <p>평당</p>
                            <p>{auctionData?.est_price_per_pyong}</p>
                        </div>
                    </div>
                    <div className="descBox">
                        <p className="appraisalDateText">감정평가 기준일</p>
                        <p className="appraisalDateContentText">{auctionData?.appraisal_date}</p>
                    </div>
                </div>
            </TotalWrapper>
        </AnimatedWrapper>
    );
};

const TitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .convertButton {
        width: 50px;
        height: 26px;
    }

    h4 {
        color: #0c002c;
        font-size: 18px;
        font-weight: 600;
        line-height: 28px;
        padding-bottom: 10px;
    }
    .chartPosition {
        margin-top: 14px;
        max-width: 390px;
        overflow: hidden;
    }
    .chartWrapper {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
        font-weight: 400;
        line-height: 20px;
        text-align: center;

        .thWrapper {
            border: 1px solid #c9c9c9;
            border-right: none;
            background-color: #fbf8f8;
            color: #0c002c;
        }
        .thContainer {
            text-align: center;
            font-size: 13px;
            font-style: normal;
            font-weight: bold;
            background-color: #17366d;
            white-space: nowrap;
            padding: 5px;
            color: #fff;
            line-height: 24px;
            border-right: 1px solid #c9c9c9;
        }
        .tdWrapper {
            border: 1px solid #c9c9c9;
            border-right: none;
            min-height: 52px;
        }
        .greyText01 {
            color: #505050;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 24px;
        }
        .tdContainer {
            border-right: 1px solid #c9c9c9;
            padding: 6px;
            white-space: pre-wrap;
            word-break: break-word;
        }
        .tdBlueContainer {
            border: 1px solid #c9c9c9;

            td {
                padding: 12px 6px;
                padding-left: 40px;
                background-color: #f6faff;
                color: #0c002c;
                font-size: 14px;
                font-weight: 400;
                line-height: 24px;
                text-align: left;
            }
        }
        .tdGreyContainer {
            border: 1px solid #c9c9c9;
            background-color: #f0f0f0;
            padding: 12px;
            padding-left: 40px;
            color: #0c002c;
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;
            white-space: pre-wrap;
            text-align: left;
        }
        .tdPinkContainer {
            border: 1px solid #c9c9c9;
            padding: 6px;
        }

        .tdEstPositionContainer {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: stretch;
        }

        .tdEstPosition {
            display: flex;
            flex-direction: row;
            justify-content: center;
            flex: 1;
            vertical-align: middle;
            width: 100%;
            color: #0c002c;
            text-align: center;
            font-size: 13px;
            font-style: normal;
            font-weight: 400;

            p {
                padding: 6px;
                width: 100%;
                box-sizing: border-box;
                display: flex;
                justify-content: center;
                align-items: center;
            }

            p:last-child {
                border-left: 1px solid #c9c9c9;
                justify-content: start;
            }
        }

        .tdEstPosition.area {
            p:first-child {
                width: 72px;
                min-width: 71px;
                box-sizing: border-box;
            }
        }

        .tdEstPosition.estPrice {
            border-left: 1px solid #c9c9c9;

            p:first-child {
                min-width: 60px;
                box-sizing: border-box;
            }
        }
    }

    .cntContainer {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
    }
    .cntBox {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        width: fit-content;
        height: 40px;
        padding: 0 6px;
        border-radius: 8px;
        background-color: #ebfbfa;
    }

    .cntBox.land {
        background-color: #e3f5e2;
    }
    .cntBox.build {
        background-color: #e0f2fe;
    }
    .cntBox.colBuild {
        background-color: #e0f2fe;
    }
    .cntBox.etc {
        background-color: #f7f8fa;
        border: 1px solid #c9c9c9;
    }
    .cntText {
        color: #0c002c;
        text-align: center;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;
        margin-left: 6px;
    }

    @media screen and (max-width: 768px) {
        width: 100%;
        box-sizing: border-box;

        .chartPosition {
            max-width: none;
        }

        .chartWrapper {
            width: 100%;
            & > colgroup {
                & > col:nth-of-type(1) {
                    width: 14%;
                }
                & > col:nth-of-type(2) {
                    width: 20%;
                }
                & > col:nth-of-type(3) {
                    width: 52%;
                }
                & > col:nth-of-type(4) {
                    width: 14%;
                }
            }
        }
    }
`;

const TotalWrapper = styled.div`
    display: flex;
    box-sizing: border-box;
    width: 100%;
    height: 111px;
    background-color: #f6faff;
    margin-top: 14px;
    padding: 0 20px;
    border-radius: 8px;

    .totalContainer {
        width: 100%;
        display: flex;
        align-items: center;
        flex-direction: column;
    }
    .totalBox {
        width: 100%;
        padding: 9px 0;
        border-bottom: 1px solid #dadada;
        margin-bottom: 8px;
    }
    .descBox {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        span {
            color: #282828;
            font-size: 16px;
            font-style: normal;
            font-weight: 700;
            line-height: 26px;
        }
        b {
            color: #0c002c;
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: 26px;
        }
        p {
            color: #3c3c3c;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 24px;
        }
        .appraisalDateText {
            color: #282828;
            font-size: 14px;
            font-weight: 700;
            line-height: 24px;
        }
        .appraisalDateContentText {
            color: #0c002c;
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;
        }
    }

    @media screen and (max-width: 768px) {
        max-width: none;
        .totalContainer {
            width: 100%;
            .totalBox {
                width: 100%;
                .descBox {
                    width: 100%;
                }
            }
            .descBox {
                width: 100%;
            }
        }
    }
`;

const TdContainerS = styled.td<{ $type: string }>`
    border-right: 1px solid #c9c9c9;
    padding: 6px;
    white-space: pre-wrap;
    word-break: break-word;

    background-color: ${(props) =>
        props.$type === "대지지분" || props.$type === "대지권" || props.$type === "토지"
            ? "#E3F5E2"
            : props.$type === "건물" || props.$type === "집합건물"
            ? "#E0F2FE"
            : ""};
`;

const BlueText = styled.p`
    color: #0047ff;
    text-align: left;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
`;

export default AuctionSaleItemPreview;
